import { format, isAfter, isValid, parse, parseISO } from "date-fns";
import { enUS, de, es, bg, cs, fr, hr, hu, nl, nlBE, pl, pt, ro, ru, sk, sr, tr, uk, it } from "date-fns/locale";

import type { Day, FormatOptions } from "date-fns";

const locales = {
	"bg-BG": bg,
	"cs-CZ": cs,
	"de-DE": de,
	"en-US": enUS,
	"es-ES": es,
	"fr-FR": fr,
	"fr-BE": fr,
	"hr-HR": hr,
	"hu-HU": hu,
	"it-IT": it,
	"nl-BE": nlBE,
	"nl-NL": nl,
	"pl-PL": pl,
	"pt-PT": pt,
	"ro-MD": ro,
	"ro-RO": ro,
	"ru-KZ": ru,
	"ru-RU": ru,
	"sk-SK": sk,
	"sr-RS": sr,
	"tr-TR": tr,
	"uk-UA": uk,
	"ur-PK": enUS,
};

const getOptions = (): FormatOptions => ({
	locale: locales[window.lang] || enUS,
});

export const getTimezoneOffset = (date: Date): number => (date.getTimezoneOffset() / 60) * -1 || 0;

export const getStartDayOfWeek = (): Day => getOptions().locale?.options?.weekStartsOn || 0;

export const isSameOrBefore = (dateLeft: Date, dateRight: Date): boolean =>
	isAfter(dateRight, dateLeft) || dateLeft.getTime() === dateRight.getTime();

export const isSameOrAfter = (dateLeft: Date, dateRight: Date): boolean =>
	isAfter(dateLeft, dateRight) || dateLeft.getTime() === dateRight.getTime();

export const parseDate = (dateString: string | null | undefined, formatString?: string): Date => {
	if (!dateString) {
		return new Date(NaN);
	}

	try {
		return formatString ? parse(dateString, formatString, new Date()) : parseISO(dateString);
	} catch (error) {
		return new Date(NaN);
	}
};

export const formatLocalDate = (date?: Date | null, formatString = "P"): string => {
	if (!date || !isValid(date)) {
		return "Invalid Date";
	}

	return format(date, formatString, getOptions());
};

export const formatLocalTime = (utcDateTime: string): string => {
	return formatLocalDate(parseDate(utcDateTime), "HH:mm");
};

export const formatUTCTime = (date: Date): string => {
	if (!isValid(date)) {
		return "Invalid Time";
	}

	const hoursPadded = String(date.getUTCHours()).padStart(2, "0");
	const minutesPadded = String(date.getUTCMinutes()).padStart(2, "0");
	const secondsPadded = String(date.getUTCSeconds()).padStart(2, "0");
	return `${hoursPadded}:${minutesPadded}:${secondsPadded}`;
};

export const formatUTCTimestamp = (date: Date): string | null => {
	if (!isValid(date)) {
		return null;
	}

	return date.toISOString();
};

export const formatUTCTimestampPretty = (date: Date): string | null => {
	if (!isValid(date)) {
		return null;
	}

	const monthPadded = String(date.getUTCMonth() + 1).padStart(2, "0");
	const dayPadded = String(date.getUTCDate()).padStart(2, "0");
	const hoursPadded = String(date.getUTCHours()).padStart(2, "0");
	const minutesPadded = String(date.getUTCMinutes()).padStart(2, "0");
	const secondsPadded = String(date.getUTCSeconds()).padStart(2, "0");

	return `${date.getUTCFullYear()}-${monthPadded}-${dayPadded} ${hoursPadded}:${minutesPadded}:${secondsPadded} UTC`;
};
